import { elementCenter, elementContains, elementOutline, } from "./elements.js";
/**
 * El campus, en el orden en que se recorre desde TAWS.
 *
 * Dejó de ser geografía de relleno: cada sala es un sitio real de ESPOL y las puertas
 * siguen los caminos que existen. Sales de TAWS (Bloque 16) a la plazoleta de la
 * FIEC, y de ahí el campus se abre en tres: al oeste el Sweet & Coffee y las aulas
 * —con el pasillo techado a la FIMCP—, al este la AEFIEC y el decanato, y pasando el
 * decanato el parqueo, que da al Parque ¡Ajá!, al STEM y al paradero. El bus lleva a
 * UBEP, y pegado a UBEP está el coliseo.
 *
 * Agregar una sala es una entrada acá más su escenario procedural (o un fondo en el
 * manifest del cliente). Nada de código de servidor.
 *
 * Estar en esta lista no es estar abierta al juego: eso lo decide `OPEN_ROOM_IDS`.
 */
export const ROOM_IDS = [
    "taws",
    "plazoleta",
    "sweet",
    "sweetpatio",
    "aulas",
    "fimcp",
    "aefiec",
    "bloque11b",
    "bloque11a",
    "salida11a",
    "fiecvieja",
    "fiecviejasur",
    "decanato",
    "parqueo",
    "parque",
    "stem",
    "paradero",
    "ubep",
    "coliseo",
    "contenedores",
    "taws2",
    "taller",
];
export const DEFAULT_ROOM = "taws";
/**
 * Las salas abiertas al juego.
 *
 * Se escriben una por línea para que cerrar una sea borrar una línea: mientras se
 * construye un sitio, se le quita de aquí y deja de existir para el jugador — no se
 * le dibuja la puerta, no se despacha el cruce y su join se rechaza. Vale para todos,
 * cuentas dev incluidas: quien construye tiene que ver lo mismo que el jugador.
 *
 * Es una lista aparte y no un campo de `RoomDef` a propósito: `rooms.ts` lo reescribe
 * el editor de mapas entero, y esto no es geometría sino una decisión de producto.
 * Un flag por sala sería una cosa más que puede volver distinta de una sesión de F2.
 */
export const OPEN_ROOM_IDS = [
    "taws",
    "plazoleta",
    "sweet",
    "sweetpatio",
    "aulas",
    "fimcp",
    "aefiec",
    "bloque11b",
    "bloque11a",
    "salida11a",
    "fiecvieja",
    "fiecviejasur",
    "decanato",
    "parqueo",
    "parque",
    "stem",
    "paradero",
    "ubep",
    "coliseo",
    "contenedores",
    "taws2",
    /*
     * `taller` NO va en esta lista, y ese es su punto.
     *
     * Es la mesa de trabajo del editor: se entra por el join directo que abre la marca
     * `dev`, no por una puerta. Dejarla cerrada es lo que hace que un jugador no pueda
     * llegar ni pidiéndola por su id, y que el campus siga viéndose igual para quien lo
     * construye — que es lo que esta lista defiende.
     */
];
/**
 * ¿Se puede llegar a esta sala?
 *
 * Lo preguntan los tres lados de la puerta: el cliente para no dibujar una zona que
 * no responde, la sala para no despachar el cruce, y `onAuth` para que un cliente
 * modificado tampoco entre pidiendo el id a secas.
 */
export function isOpenRoom(id) {
    return typeof id === "string" && OPEN_ROOM_IDS.includes(id);
}
export const ROOMS = {
    taws: {
        id: "taws",
        name: "TAWS",
        art: "taws",
        /**
         * Las baldosas del pasillo, medidas sobre `art/rooms/taws.png`.
         *
         * Un trapecio, porque la sala está en perspectiva de un punto: angosto al fondo,
         * ancho al frente. Los cuatro vértices salen de las dos líneas donde la pared se
         * junta con el piso en el dibujo, no de mirar la imagen a ojo — con un pasillo
         * tan inclinado, un par de píxeles de error al fondo son treinta al frente.
         *
         * Está metido un poco hacia adentro a propósito: sobre la línea misma el avatar
         * pisa el zócalo, y al frente se corta contra el borde de la pantalla.
         *
         * La esquina de abajo a la derecha se detiene DONDE EMPIEZA el mesón de PCs, que es
         * sólido. Estuvo un tiempo en (925, 538), ya debajo del mueble, y eso convertía el
         * vértice del piso en un sitio al que el jugador puede apuntar y no llegar nunca:
         * camina, choca y se queda empujando contra algo que no se aparta. El extremo del
         * piso tiene que ser pisable, o deja de ser el extremo del piso.
         *
         * Que se haya acercado tanto al mesón sí es nuevo, y es lo que permitió el polígono
         * de colisión del mueble: con la caja cruda, todo lo que hay de x 634 en adelante
         * estorbaba. Ahora sólo estorba el mueble, y el pasillo llega hasta su borde.
         *
         * **Si mueves el polígono del mesón, este vértice va detrás.** Los dos describen la
         * misma línea del dibujo —dónde el piso se mete debajo del mueble— desde los dos
         * lados, así que tocar uno sin el otro devuelve el problema: o el vértice queda
         * dentro del mesón y es inalcanzable, o queda corto y deja una franja de piso
         * pintado por la que no se puede caminar. Refínalos juntos con F2.
         */
        walkable: [
            { x: 338, y: 244 },
            { x: 596, y: 248 },
            { x: 616, y: 265 },
            { x: 624, y: 111 },
            { x: 699, y: 109 },
            { x: 690, y: 337 },
            { x: 941, y: 540 },
            { x: 39, y: 528 },
        ],
        spawn: { x: 470, y: 400 },
        elements: [
            {
                /* Una zona de piso DELANTE de la puerta dibujada, igual que `taws-plazoleta`:
                   la puerta de madera del fondo queda fuera del área caminable, y una zona
                   sobre ella sería inalcanzable. Geometría a ojo: aféinala con F2. */
                id: "taws-taws2",
                kind: "puerta",
                label: "sala2",
                rect: { x: 622, y: 140, width: 74, height: 166 },
                shape: "polygon",
                entry: { x: 613, y: 287 },
                points: [
                    { x: 627, y: 140 },
                    { x: 696, y: 150 },
                    { x: 691, y: 275 },
                    { x: 689, y: 306 },
                    { x: 676, y: 301 },
                    { x: 653, y: 301 },
                    { x: 622, y: 265 },
                ],
                showLabel: false,
                /* No bloquea: es una zona de piso en medio del pasillo, y el tipo bloquea por
                   defecto. Sólida, parte en dos el único tramo por el que se camina. */
                solid: false,
                to: "taws2",
            },
            {
                /*
                 * El mesón de PCs, contra la pared derecha.
                 *
                 * Pide su dibujo por nombre en vez de usar el del tipo: una mesa cualquiera
                 * del campus sigue siendo una mesa cualquiera, y esta es la de un laboratorio.
                 *
                 * **La colisión no vive acá**: vive en `ART_SHAPES["mesa-pcs"]`, en
                 * `artShapes.ts`. El mesón está dibujado en perspectiva —una banda diagonal que
                 * baja de izquierda a derecha— y deja dos esquinas de su caja completamente
                 * vacías; con la caja cruda el jugador chocaba contra el aire de encima de los
                 * monitores y contra el hueco de piso de delante.
                 *
                 * Que la silueta esté en el catálogo de arte y no en esta instancia es lo que
                 * hace que se moldee UNA vez: la forma es del mesón, no del sitio donde se puso,
                 * así que la heredan todas sus instancias, incluidas las que se coloquen mañana.
                 * Aquí sólo queda su caja, que es lo propio de este mesón concreto.
                 */
                id: "taws-mesa",
                kind: "mesa",
                rect: { x: 637, y: 266, width: 325, height: 273 },
                art: "mesa-pcs",
            },
            {
                id: "taws-juego",
                kind: "juego",
                rect: { x: 379, y: 193, width: 185, height: 91 },
                layer: "back",
                art: "mesa",
                gameId: "demo",
            },
            {
                id: "taws-silla-2",
                kind: "silla",
                rect: { x: 618, y: 320, width: 87, height: 84 },
                surface: {
                    polygon: [
                        { x: 646, y: 367 },
                        { x: 662, y: 367 },
                        { x: 662, y: 383 },
                        { x: 646, y: 383 },
                    ],
                    height: 16,
                    accessEdges: [],
                    fallsAtEdge: true,
                },
            },
            {
                /*
                 * La superficie tiene que caer SOBRE el asiento dibujado.
                 *
                 * Estaba a 120px a la derecha del mueble: tocabas la silla y el servidor te
                 * plantaba sentado en mitad del pasillo, flotando los 16px de la altura. El
                 * asiento es de donde sale el punto de aproximación de una silla —`approach`
                 * apunta a la superficie cuando el objeto no bloquea— así que una superficie
                 * desplazada no es un detalle visual: es a dónde te manda a caminar.
                 */
                id: "taws-silla",
                kind: "silla",
                rect: { x: 662, y: 401, width: 97, height: 94 },
                surface: {
                    polygon: [
                        { x: 702, y: 438 },
                        { x: 718, y: 438 },
                        { x: 718, y: 454 },
                        { x: 702, y: 454 },
                    ],
                    height: 16,
                    accessEdges: [],
                    fallsAtEdge: true,
                },
            },
            {
                /*
                 * La boca del pasillo: la franja de piso del borde de abajo, por donde se sale
                 * del TAWS hacia la plazoleta caminando HACIA la cámara.
                 *
                 * Estuvo describiendo la puerta de madera del fondo —"ocupa x 626-699 y apoya
                 * en y 313"— desde antes de que esa puerta tuviera dueño. Hoy lo tiene: esas
                 * son las coordenadas de `taws-taws2`, la de WiDS. Dos elementos citando la
                 * misma geometría mandan al siguiente a mover la puerta equivocada, así que el
                 * comentario vuelve a describir la zona que tiene debajo.
                 *
                 * Es un polígono y no un rectángulo porque el piso está en perspectiva: un
                 * rect se dibujaría plano sobre unas baldosas que se estrechan, y se leería
                 * como un cartel flotando en vez de como un felpudo delante de la puerta.
                 */
                id: "taws-plazoleta",
                kind: "puerta",
                label: "FIEC",
                rect: { x: 246, y: 453, width: 176, height: 70 },
                shape: "polygon",
                showLabel: false,
                points: [
                    { x: 263, y: 453 },
                    { x: 408, y: 454 },
                    { x: 422, y: 523 },
                    { x: 246, y: 522 },
                ],
                to: "plazoleta",
            },
            {
                id: "taws-mesa-2",
                kind: "mesa",
                rect: { x: 43, y: 240, width: 305, height: 277 },
                art: "mesa-taws",
            },
            {
                id: "taws-plazoleta-2",
                kind: "puerta",
                label: "FIEC Vieja",
                rect: { x: 483, y: 457, width: 156, height: 64 },
                to: "fiecvieja",
                layer: "back",
            },
        ],
    },
    /*
     * La segunda sala de TAWS, la de WiDS. Se ve DESDE su puerta, así que el umbral
     * queda detrás de la cámara: la vuelta es una zona en el borde de abajo.
     */
    taws2: {
        id: "taws2",
        name: "TAWS · WiDS",
        art: "taws2",
        /* Un trapecio conservador, metido dentro del piso dibujado. Sin afinar. */
        walkable: [
            { x: 350, y: 340 },
            { x: 700, y: 340 },
            { x: 900, y: 530 },
            { x: 60, y: 530 },
        ],
        spawn: { x: 480, y: 450 },
        elements: [
            {
                /*
                 * La vuelta a TAWS: oculta y NO sólida.
                 *
                 * Oculta porque la puerta de verdad está detrás de la cámara y no hay nada que
                 * dibujar; se cruza caminando dentro. Y `solid: false` porque el tipo bloquea
                 * por defecto, y una oculta que además bloquee es un muro invisible — el
                 * validador lo rechaza por eso mismo.
                 */
                id: "taws2-taws",
                kind: "puerta",
                label: "TAWS",
                rect: { x: 380, y: 480, width: 240, height: 45 },
                hidden: true,
                solid: false,
                /*
                 * DELANTE de la zona, no dentro.
                 *
                 * Sin esto se aterriza en su centro —lo que hace la derivación con una puerta
                 * que no bloquea— y como ésta es oculta, que se cruza con sólo pisarla, el
                 * primer paso te devolvía a TAWS. Entrar y rebotar, sin que nada fallara.
                 */
                entry: { x: 500, y: 465 },
                to: "taws",
            },
        ],
    },
    /*
     * La explanada de la FIEC: el cruce del que cuelga toda la facultad.
     *
     * Cinco puertas, que es el nudo más denso del campus. Por eso el piso es un
     * trapecio ancho y las zonas están repartidas por los cuatro costados: apiladas
     * cerca unas de otras, caminar hasta una acabaría cruzando otra.
     */
    plazoleta: {
        id: "plazoleta",
        name: "Plazoleta FIEC",
        art: "plazoleta",
        walkable: [
            { x: 173, y: 216 },
            { x: 409, y: 231 },
            { x: 480, y: 220 },
            { x: 537, y: 220 },
            { x: 693, y: 231 },
            { x: 676, y: 297 },
            { x: 797, y: 357 },
            { x: 960, y: 462 },
            { x: 960, y: 537 },
            { x: 169, y: 530 },
            { x: 0, y: 427 },
            { x: 0, y: 297 },
        ],
        spawn: { x: 480, y: 375 },
        elements: [
            {
                id: "plazoleta-aulas",
                kind: "puerta",
                label: "Edificio 11C · Aulas",
                rect: { x: 377, y: 223, width: 101, height: 35 },
                shape: "polygon",
                entry: { x: 409, y: 264 },
                points: [
                    { x: 377, y: 252 },
                    { x: 478, y: 223 },
                    { x: 471, y: 258 },
                ],
                to: "aulas",
            },
            {
                id: "plazoleta-sweet",
                kind: "puerta",
                label: "Sweet & Coffee",
                rect: { x: 791, y: 407, width: 169, height: 130 },
                shape: "polygon",
                entry: { x: 790, y: 456 },
                points: [
                    { x: 859, y: 407 },
                    { x: 960, y: 462 },
                    { x: 960, y: 537 },
                    { x: 791, y: 530 },
                ],
                to: "sweetpatio",
            },
            {
                id: "plazoleta-arbol",
                kind: "arbol",
                rect: { x: 359, y: -1, width: 358, height: 332 },
                layer: "back",
            },
            {
                id: "plazoleta-decanato",
                kind: "puerta",
                label: "Edificio 11A · Decanato",
                rect: { x: 117, y: 221, width: 171, height: 39 },
                shape: "polygon",
                entry: { x: 211, y: 267 },
                layer: "back",
                points: [
                    { x: 175, y: 221 },
                    { x: 288, y: 227 },
                    { x: 202, y: 260 },
                    { x: 117, y: 249 },
                ],
                to: "bloque11a",
            },
            {
                id: "plazoleta-taws",
                kind: "puerta",
                label: "TAWS",
                rect: { x: 602, y: 229, width: 90, height: 62 },
                shape: "polygon",
                hidden: true,
                solid: false,
                showLabel: false,
                points: [
                    { x: 608, y: 229 },
                    { x: 692, y: 233 },
                    { x: 655, y: 291 },
                    { x: 602, y: 277 },
                ],
                /*
                 * Declarado, y es el primero del campus que lo necesita.
                 *
                 * Derivar el aterrizaje es salir del umbral EN LÍNEA RECTA hacia el spawn, y
                 * desde aquí esa línea entra de lleno en "plazoleta-arbol" —que mide 358x332 y
                 * se come el centro de la plaza— sin volver a salir dentro del alcance de la
                 * búsqueda. La búsqueda se rendía y devolvía el spawn, así que venir de TAWS te
                 * dejaba en mitad de la plazoleta en vez de en la puerta por la que acababas de
                 * entrar. Justo debajo del umbral sí hay suelo libre; es sólo que no está en esa
                 * recta.
                 */
                entry: { x: 647, y: 310 },
                to: "taws",
            },
        ],
    },
    /*
     * El local del patio. Es la TIENDA: el mostrador de aquí es lo que la abre, y por
     * eso comprar dejó de ser un botón para ser un sitio al que se va.
     */
    sweet: {
        id: "sweet",
        name: "Sweet & Coffee",
        art: "sweet",
        walkable: [
            { x: 90, y: 255 },
            { x: 870, y: 255 },
            { x: 870, y: 495 },
            { x: 90, y: 495 },
        ],
        /*
         * Estuvo en y 400 y ahí NO era un punto legal: el cuerpo del jugador —su punto y las
         * ocho sondas a 12px— se metía en `aefiec-plazoleta`, que es sólida. Se salía andando
         * igual, pero por el resquicio de `advance` que deja moverse a quien ya está en un
         * sitio imposible, no porque el sitio valiera. Un spawn que depende de esa vía de
         * escape es un spawn del que `canStand` dice que no.
         *
         * 15px arriba: 13 de margen hasta la puerta y 18 hasta la mesa.
         */
        spawn: { x: 480, y: 385 },
        elements: [
            {
                /* El mostrador es sólido y se atiende por delante: sin `anchor` el jugador
                   podría acabar atendido desde detrás de la caja. */
                id: "sweet-mostrador",
                kind: "mostrador",
                rect: { x: 330, y: 270, width: 300, height: 70 },
                /* Delante de la caja y a un paso del borde: más lejos deja de estar al
                   alcance, que se mide contra el contorno con el radio de interacción. */
                anchor: { x: 480, y: 356 },
            },
            {
                id: "sweet-mesa-1",
                kind: "mesa",
                rect: { x: 130, y: 300, width: 90, height: 70 },
            },
            {
                id: "sweet-mesa-2",
                kind: "mesa",
                rect: { x: 740, y: 300, width: 90, height: 70 },
            },
            {
                id: "sweet-silla-1",
                kind: "silla",
                rect: { x: 130, y: 380, width: 80, height: 60 },
                surface: {
                    polygon: [
                        { x: 142, y: 402 },
                        { x: 198, y: 402 },
                        { x: 198, y: 430 },
                        { x: 142, y: 430 },
                    ],
                    height: 16,
                    accessEdges: [],
                    fallsAtEdge: true,
                },
            },
            {
                id: "sweet-silla-2",
                kind: "silla",
                rect: { x: 745, y: 380, width: 80, height: 60 },
                surface: {
                    polygon: [
                        { x: 757, y: 402 },
                        { x: 813, y: 402 },
                        { x: 813, y: 430 },
                        { x: 757, y: 430 },
                    ],
                    height: 16,
                    accessEdges: [],
                    fallsAtEdge: true,
                },
            },
            {
                id: "sweet-plazoleta",
                kind: "puerta",
                label: "Salir a la plazoleta",
                rect: { x: 405, y: 410, width: 150, height: 60 },
                shape: "arch",
                to: "plazoleta",
            },
            {
                // Cierra el anillo corto alrededor de la plazoleta: sin esto, ir del café a la
                // asociación obligaba a volver a cruzar el centro.
                id: "sweet-aefiec",
                kind: "puerta",
                label: "Edificio 11B · AEFIEC",
                rect: { x: 580, y: 380, width: 150, height: 60 },
                shape: "arch",
                to: "aefiec",
            },
            {
                /* PROVISIONAL — hueco libre entre la silla-1 y el mostrador, a ojo. Se ajusta
                   con F2 mirando `sweetpatio.jpg`. */
                id: "sweet-sweetpatio",
                kind: "puerta",
                label: "Terraza",
                rect: { x: 250, y: 420, width: 130, height: 50 },
                to: "sweetpatio",
            },
        ],
    },
    /*
     * La terraza cubierta de Sweet & Coffee: mesas y sillas ya dibujadas en el fondo,
     * columnas amarillas sosteniendo el techo verde.
     *
     * PROVISIONAL de punta a punta, igual que `fiecviejasur` — walkable, spawn y la
     * puerta son un rectángulo a ojo. Las mesas del fondo no tienen elemento todavía:
     * se agregan con F2, con `backdrop: true` si se dejan como están dibujadas.
     */
    sweetpatio: {
        id: "sweetpatio",
        name: "Terraza Sweet & Coffee",
        art: "sweetpatio",
        walkable: [
            { x: 0, y: 203 },
            { x: 250, y: 204 },
            { x: 253, y: 254 },
            { x: 870, y: 394 },
            { x: 960, y: 258 },
            { x: 959, y: 540 },
            { x: 0, y: 539 },
        ],
        spawn: { x: 400, y: 420 },
        elements: [
            {
                /* Vuelta a Sweet & Coffee, en el borde de arriba. */
                id: "sweetpatio-sweet",
                kind: "puerta",
                label: "Sweet & Coffee",
                rect: { x: 1, y: 207, width: 249, height: 48 },
                entry: { x: 144, y: 265 },
                to: "plazoleta",
            },
            {
                id: "sweetpatio-taws",
                kind: "puerta",
                label: "FIEC Vieja",
                rect: { x: 873, y: 259, width: 87, height: 143 },
                shape: "polygon",
                entry: { x: 911, y: 405 },
                points: [
                    { x: 960, y: 259 },
                    { x: 959, y: 402 },
                    { x: 873, y: 394 },
                ],
                to: "fiecvieja",
            },
        ],
    },
    /* Bloque de aulas teóricas. De aquí sale el pasillo techado a la FIMCP. */
    aulas: {
        id: "aulas",
        name: "Edificio 11C",
        art: "aulas",
        walkable: [
            { x: 90, y: 255 },
            { x: 870, y: 255 },
            { x: 870, y: 495 },
            { x: 90, y: 495 },
        ],
        spawn: { x: 480, y: 390 },
        elements: [
            {
                id: "aulas-escritorio",
                kind: "mesa",
                rect: { x: 560, y: 285, width: 140, height: 65 },
            },
            {
                id: "aulas-pupitre-2",
                kind: "silla",
                rect: { x: 290, y: 300, width: 70, height: 60 },
                surface: {
                    polygon: [
                        { x: 306, y: 328 },
                        { x: 334, y: 328 },
                        { x: 334, y: 344 },
                        { x: 306, y: 344 },
                    ],
                    height: 16,
                    accessEdges: [],
                    fallsAtEdge: true,
                },
            },
            {
                id: "aulas-pupitre-3",
                kind: "silla",
                rect: { x: 400, y: 300, width: 70, height: 60 },
                surface: {
                    polygon: [
                        { x: 413, y: 335 },
                        { x: 444, y: 335 },
                        { x: 444, y: 351 },
                        { x: 413, y: 351 },
                    ],
                    height: 16,
                    accessEdges: [],
                    fallsAtEdge: true,
                },
            },
            {
                id: "aulas-pupitre-1",
                kind: "silla",
                rect: { x: 179, y: 302, width: 70, height: 60 },
                surface: {
                    polygon: [
                        { x: 192, y: 333 },
                        { x: 227, y: 333 },
                        { x: 227, y: 349 },
                        { x: 192, y: 349 },
                    ],
                    height: 16,
                    accessEdges: [],
                    fallsAtEdge: true,
                },
            },
            {
                /* El pasillo techado no es una sala: es lo que hace larga esta puerta. La
                   etiqueta lleva el recorrido, que es lo único que el jugador necesita. */
                id: "aulas-fimcp",
                kind: "puerta",
                label: "Pasillo techado · FIMCP",
                rect: { x: 660, y: 380, width: 150, height: 60 },
                solid: false,
                hidden: true,
                to: "fimcp",
            },
            {
                /* Sitio PROVISIONAL, como su gemela en la plazoleta. Acomódala con F2. */
                id: "aulas-fiecvieja",
                kind: "puerta",
                label: "FIEC Vieja",
                rect: { x: 120, y: 400, width: 150, height: 60 },
                shape: "arch",
                to: "fiecvieja",
            },
            {
                id: "aulas-plazoleta",
                kind: "puerta",
                label: "Salir a la plazoleta",
                rect: { x: 405, y: 410, width: 150, height: 60 },
                shape: "arch",
                to: "plazoleta",
            },
        ],
    },
    /* La otra facultad al final del pasillo. Taller: mesas grandes y sitio para pasar. */
    fimcp: {
        id: "fimcp",
        name: "FIMCP",
        art: "fimcp",
        walkable: [
            { x: 90, y: 255 },
            { x: 870, y: 255 },
            { x: 870, y: 495 },
            { x: 90, y: 495 },
        ],
        spawn: { x: 400, y: 400 },
        elements: [
            {
                /* La sede de los clubes de la FIMCP: ROBOTA, ASME, MAEC y los demás del taller. */
                id: "fimcp-clubes",
                kind: "directorio",
                facultyId: "fimcp",
                rect: { x: 120, y: 280, width: 130, height: 90 },
            },
            {
                id: "fimcp-banco-1",
                kind: "mesa",
                rect: { x: 380, y: 280, width: 180, height: 70 },
            },
            {
                id: "fimcp-banco-2",
                kind: "mesa",
                rect: { x: 620, y: 280, width: 180, height: 70 },
            },
            {
                id: "fimcp-aulas",
                kind: "puerta",
                label: "Pasillo techado · FIEC",
                rect: { x: 150, y: 380, width: 150, height: 60 },
                solid: false,
                hidden: true,
                to: "aulas",
            },
            {
                // FIMCP colgaba de un AULA de FIEC, y era su única entrada. Una facultad se cuelga
                // de la red de vías, no del salón de clases de otra.
                id: "fimcp-parqueo",
                kind: "puerta",
                label: "Parqueo FIEC",
                rect: { x: 700, y: 380, width: 150, height: 60 },
                shape: "arch",
                to: "parqueo",
            },
            {
                /*
                 * Arrimado, no en mitad del taller.
                 *
                 * En (600,400) caía sobre la línea del spawn a la puerta del parqueo —la que se
                 * agregó para que la facultad colgara de la red de vías y no de un aula ajena— y
                 * la dejaba sin poder usarse. El asiento baja con él: son el mismo mueble.
                 */
                id: "fimcp-taburete",
                kind: "silla",
                rect: { x: 600, y: 440, width: 70, height: 60 },
                surface: {
                    polygon: [
                        { x: 612, y: 462 },
                        { x: 658, y: 462 },
                        { x: 658, y: 490 },
                        { x: 612, y: 490 },
                    ],
                    height: 16,
                    accessEdges: [],
                    fallsAtEdge: true,
                },
            },
        ],
    },
    /* La asociación de estudiantes: sala de reunión, sillas alrededor de una mesa. */
    aefiec: {
        id: "aefiec",
        name: "AEFIEC · Bloque 11B",
        art: "aefiec",
        walkable: [
            { x: 90, y: 255 },
            { x: 870, y: 255 },
            { x: 870, y: 495 },
            { x: 90, y: 495 },
        ],
        /* El cuerpo debe quedar fuera de la puerta de salida, incluso antes de moverse. */
        spawn: { x: 480, y: 385 },
        elements: [
            {
                id: "aefiec-mesa",
                kind: "mesa",
                rect: { x: 390, y: 285, width: 180, height: 70 },
            },
            {
                /* Subida 35px para despejar el paso a FIEC Vieja desde el spawn.
                   El asiento se desplaza con la silla; el dibujo sale de esta misma caja. */
                id: "aefiec-silla-1",
                kind: "silla",
                rect: { x: 300, y: 265, width: 70, height: 60 },
                surface: {
                    polygon: [
                        { x: 312, y: 287 },
                        { x: 358, y: 287 },
                        { x: 358, y: 315 },
                        { x: 312, y: 315 },
                    ],
                    height: 16,
                    accessEdges: [],
                    fallsAtEdge: true,
                },
            },
            {
                id: "aefiec-silla-2",
                kind: "silla",
                rect: { x: 590, y: 300, width: 70, height: 60 },
                surface: {
                    polygon: [
                        { x: 602, y: 322 },
                        { x: 648, y: 322 },
                        { x: 648, y: 350 },
                        { x: 602, y: 350 },
                    ],
                    height: 16,
                    accessEdges: [],
                    fallsAtEdge: true,
                },
            },
            {
                /* La sede de los clubes de la FIEC. Es el mismo tablón del patio filtrado por la
                   facultad del edificio: acá sólo salen TAWS, AEFIEC, IEEE y los suyos. */
                id: "aefiec-clubes",
                kind: "directorio",
                facultyId: "fiec",
                rect: { x: 720, y: 390, width: 130, height: 90 },
            },
            {
                /* Separada de la puerta de Sweet & Coffee. La silla de la izquierda
                   deja libre el trayecto desde el spawn hasta este umbral. */
                id: "aefiec-fiecvieja",
                kind: "puerta",
                label: "FIEC Vieja",
                rect: { x: 100, y: 300, width: 150, height: 60 },
                shape: "arch",
                to: "fiecvieja",
            },
            {
                id: "aefiec-plazoleta",
                kind: "puerta",
                label: "Salir a la plazoleta",
                rect: { x: 405, y: 410, width: 150, height: 60 },
                shape: "arch",
                to: "plazoleta",
            },
            {
                id: "aefiec-sweet",
                kind: "puerta",
                label: "Sweet & Coffee",
                rect: { x: 150, y: 380, width: 150, height: 60 },
                shape: "arch",
                to: "sweet",
            },
            {
                /* PROVISIONAL — segundo intento. El primero (700,270) cruzaba la recta del
                   spawn contra la silla; el segundo (575,385) arreglaba eso pero se metía en
                   el camino hacia el tablón de clubes. Se ajusta con F2 mirando
                   `bloque11b.jpg`. */
                id: "aefiec-bloque11b",
                kind: "puerta",
                label: "Pasillo",
                rect: { x: 700, y: 255, width: 130, height: 40 },
                to: "bloque11b",
            },
        ],
    },
    /*
     * El pasillo/atrio de Bloque 11B, el edificio de la AEFIEC: tragaluz, tres puertas
     * al fondo y una salida al campus a la izquierda.
     *
     * PROVISIONAL de punta a punta, igual que `fiecviejasur` y `sweetpatio` — walkable,
     * spawn y la puerta son un rectángulo a ojo. Las tres puertas del fondo (una se
     * dibuja abierta) y la salida al exterior por la izquierda todavía no son
     * elementos: se agregan con F2 mirando la imagen.
     */
    bloque11b: {
        id: "bloque11b",
        name: "Bloque 11B",
        art: "bloque11b",
        walkable: [
            { x: 194, y: 315 },
            { x: 226, y: 261 },
            { x: 291, y: 261 },
            { x: 334, y: 299 },
            { x: 602, y: 271 },
            { x: 691, y: 284 },
            { x: 932, y: 540 },
            { x: 80, y: 540 },
        ],
        spawn: { x: 480, y: 400 },
        elements: [
            {
                /* Vuelta a AEFIEC, en el borde de arriba. */
                id: "bloque11b-aefiec",
                kind: "puerta",
                label: "AEFIEC",
                rect: { x: 194, y: 261, width: 141, height: 55 },
                shape: "polygon",
                entry: { x: 290, y: 310 },
                points: [
                    { x: 226, y: 262 },
                    { x: 290, y: 261 },
                    { x: 335, y: 300 },
                    { x: 194, y: 316 },
                ],
                /* Estuvo en "plazoleta" un momento — su id, su etiqueta y este mismo
                   comentario siguen diciendo AEFIEC, así que el destino vuelve a coincidir.
                   El `entry` de arriba lo usa quien llega por `aefiec-bloque11b`. */
                to: "aefiec",
            },
            {
                /* PROVISIONAL — a ojo, dentro del tramo ancho del pasillo. Se ajusta con F2
                   mirando `bloque11a.jpg`. */
                id: "bloque11b-bloque11a",
                kind: "puerta",
                label: "Bloque 11A",
                rect: { x: 558, y: 485, width: 130, height: 55 },
                to: "bloque11a",
            },
        ],
    },
    /*
     * El vestíbulo de Bloque 11A: escalera, kiosko de pantalla, tótem con el mapa del
     * campus y el banner de ESPOL.
     *
     * PROVISIONAL de punta a punta. El kiosko, el tótem, las bancas y la escalera
     * todavía no son elementos: se agregan con F2 mirando la imagen.
     */
    bloque11a: {
        id: "bloque11a",
        name: "Bloque 11A",
        art: "bloque11a",
        walkable: [
            { x: 0, y: 511 },
            { x: 153, y: 469 },
            { x: 155, y: 449 },
            { x: 272, y: 395 },
            { x: 255, y: 291 },
            { x: 279, y: 292 },
            { x: 289, y: 303 },
            { x: 365, y: 284 },
            { x: 358, y: 251 },
            { x: 522, y: 245 },
            { x: 548, y: 258 },
            { x: 550, y: 167 },
            { x: 623, y: 230 },
            { x: 629, y: 317 },
            { x: 755, y: 337 },
            { x: 763, y: 254 },
            { x: 824, y: 235 },
            { x: 821, y: 246 },
            { x: 816, y: 308 },
            { x: 858, y: 319 },
            { x: 902, y: 275 },
            { x: 958, y: 272 },
            { x: 960, y: 539 },
            { x: 0, y: 540 },
        ],
        spawn: { x: 400, y: 420 },
        elements: [
            {
                /* Vuelta a Bloque 11B, en el borde de arriba. */
                id: "bloque11a-bloque11b",
                kind: "puerta",
                label: "Bloque 11B",
                rect: { x: 549, y: 174, width: 78, height: 111 },
                shape: "polygon",
                points: [
                    { x: 551, y: 174 },
                    { x: 620, y: 230 },
                    { x: 627, y: 285 },
                    { x: 549, y: 260 },
                ],
                to: "bloque11b",
            },
            {
                id: "bloque11a-taws",
                kind: "puerta",
                label: "TAWS",
                rect: { x: 759, y: 238, width: 62, height: 74 },
                shape: "polygon",
                entry: { x: 799, y: 316 },
                points: [
                    { x: 766, y: 254 },
                    { x: 821, y: 238 },
                    { x: 816, y: 310 },
                    { x: 759, y: 312 },
                ],
                to: "plazoleta",
            },
            {
                /* PROVISIONAL — en la franja ancha de abajo, lejos de las otras dos puertas.
                   Se ajusta con F2 en cuanto `salida11a.jpg` deje de estar borrosa abajo. */
                id: "bloque11a-salida11a",
                kind: "puerta",
                label: "Salida",
                rect: { x: 400, y: 460, width: 130, height: 50 },
                to: "salida11a",
            },
        ],
    },
    /*
     * La salida de Bloque 11A hacia el campus: el mural de la entrada, la rampa de
     * acceso y los árboles del patio.
     *
     * PROVISIONAL — walkable, spawn y la puerta siguen siendo un rectángulo a ojo. La
     * imagen ya se regeneró sin el desenfoque que tenía el primer plano, así que esto ya
     * se puede trazar de verdad con F2.
     */
    salida11a: {
        id: "salida11a",
        name: "Salida de Bloque 11A",
        art: "salida11a",
        walkable: [
            { x: 0, y: 282 },
            { x: 294, y: 300 },
            { x: 409, y: 264 },
            { x: 411, y: 230 },
            { x: 518, y: 231 },
            { x: 517, y: 264 },
            { x: 585, y: 261 },
            { x: 635, y: 285 },
            { x: 693, y: 298 },
            { x: 697, y: 315 },
            { x: 731, y: 313 },
            { x: 743, y: 331 },
            { x: 798, y: 332 },
            { x: 903, y: 355 },
            { x: 887, y: 372 },
            { x: 960, y: 384 },
            { x: 960, y: 539 },
            { x: 0, y: 540 },
        ],
        spawn: { x: 400, y: 420 },
        elements: [
            {
                /* Vuelta a Bloque 11A, en el borde de arriba. */
                id: "salida11a-bloque11a",
                kind: "puerta",
                label: "Bloque 11A",
                rect: { x: 409, y: 231, width: 107, height: 34 },
                shape: "polygon",
                points: [
                    { x: 413, y: 231 },
                    { x: 515, y: 234 },
                    { x: 516, y: 265 },
                    { x: 409, y: 265 },
                ],
                to: "bloque11a",
            },
            {
                id: "salida11a-taws",
                kind: "puerta",
                label: "Coliseo",
                rect: { x: 1, y: 356, width: 109, height: 169 },
                shape: "polygon",
                points: [
                    { x: 1, y: 375 },
                    { x: 110, y: 356 },
                    { x: 110, y: 524 },
                    { x: 1, y: 525 },
                ],
                to: "coliseo",
            },
        ],
    },
    /*
     * El edificio viejo de la FIEC, vecino de la plazoleta, del 11C y de la AEFIEC.
     *
     * Su motivo de existir es el puesto de juego: la sala es el sitio, el juego es lo
     * que se hace en ella.
     */
    fiecvieja: {
        id: "fiecvieja",
        name: "FIEC Vieja",
        art: "fiecvieja",
        walkable: [
            { x: 160, y: 266 },
            { x: 223, y: 272 },
            { x: 221, y: 211 },
            { x: 270, y: 218 },
            { x: 271, y: 282 },
            { x: 716, y: 346 },
            { x: 786, y: 272 },
            { x: 838, y: 277 },
            { x: 830, y: 352 },
            { x: 798, y: 446 },
            { x: 960, y: 493 },
            { x: 960, y: 540 },
            { x: 572, y: 540 },
            { x: 118, y: 539 },
            { x: 116, y: 525 },
            { x: 0, y: 458 },
            { x: 0, y: 299 },
            { x: 147, y: 305 },
        ],
        /* Debajo del carrito y entre las dos puertas de abajo: aparecer pegado a algo
           sólido deja al jugador empujándolo, y aparecer sobre una puerta lo saca. */
        spawn: { x: 480, y: 425 },
        elements: [
            {
                /*
                 * El puesto de la facultad: el carrito sirve el Frozen de Frutas.
                 *
                 * Las reglas y las medidas del juego están en `shared/frozen.ts`, y los topes
                 * con los que el servidor valida la partida SALEN de ahí — esta entrada sólo
                 * dice dónde está el puesto y a qué juego abre.
                 */
                id: "fiecvieja-juego",
                kind: "juego",
                rect: { x: -39, y: 334, width: 330, height: 184 },
                layer: "front",
                art: "carrito",
                gameId: "frozen",
            },
            {
                /* Sitio PROVISIONAL. Acomódala con F2. */
                id: "fiecvieja-aefiec",
                kind: "puerta",
                label: "Edificio 11B · AEFIEC",
                rect: { x: 767, y: 272, width: 71, height: 39 },
                shape: "polygon",
                points: [
                    { x: 787, y: 272 },
                    { x: 838, y: 277 },
                    { x: 834, y: 311 },
                    { x: 767, y: 298 },
                ],
                to: "aefiec",
            },
            {
                id: "fiecvieja-plazoleta",
                kind: "puerta",
                label: "FIEC Sur",
                rect: { x: 500, y: 497, width: 92, height: 40 },
                shape: "arch",
                to: "fiecviejasur",
            },
            {
                id: "fiecvieja-aulas",
                kind: "puerta",
                label: "Edificio 11C · Aulas",
                rect: { x: 221, y: 212, width: 50, height: 70 },
                shape: "polygon",
                points: [
                    { x: 221, y: 212 },
                    { x: 268, y: 218 },
                    { x: 271, y: 282 },
                    { x: 221, y: 276 },
                ],
                to: "aulas",
            },
            {
                id: "fiecvieja-taws",
                kind: "puerta",
                label: "S&C",
                rect: { x: 753, y: 444, width: 102, height: 96 },
                shape: "polygon",
                points: [
                    { x: 799, y: 444 },
                    { x: 855, y: 463 },
                    { x: 848, y: 537 },
                    { x: 753, y: 540 },
                ],
                to: "sweetpatio",
            },
        ],
    },
    /*
     * El patio al sur de FIEC Vieja: kiosko, dos edificios y las gradas.
     *
     * PROVISIONAL de punta a punta — walkable, spawn y la puerta son un rectángulo a
     * ojo. Se termina con F2 mirando `fiecviejasur.jpg`: mobiliario (kiosko, banca,
     * árboles, gradas) todavía no tiene ni un elemento, se agrega ahí mismo.
     */
    fiecviejasur: {
        id: "fiecviejasur",
        name: "Patio sur",
        art: "fiecviejasur",
        walkable: [
            { x: 0, y: 321 },
            { x: 395, y: 215 },
            { x: 619, y: 272 },
            { x: 722, y: 254 },
            { x: 826, y: 306 },
            { x: 815, y: 327 },
            { x: 900, y: 385 },
            { x: 839, y: 451 },
            { x: 960, y: 503 },
            { x: 958, y: 539 },
            { x: 0, y: 540 },
        ],
        spawn: { x: 400, y: 420 },
        elements: [
            {
                /* Vuelta a FIEC Vieja. Misma franja x que la puerta del otro lado, en el
                   borde de arriba: se entra por acá viniendo del norte. */
                id: "fiecviejasur-fiecvieja",
                kind: "puerta",
                label: "FIEC Vieja",
                rect: { x: 20, y: 476, width: 110, height: 45 },
                shape: "arch",
                entry: { x: 113, y: 469 },
                to: "fiecvieja",
            },
            {
                id: "fiecviejasur-taws",
                kind: "puerta",
                label: "TAWS",
                rect: { x: 194, y: 278, width: 110, height: 50 },
                to: "taws",
            },
        ],
    },
    /* Decanato: ventanillas de atención, y la salida al parqueo por el frente. */
    decanato: {
        id: "decanato",
        name: "Decanato · Bloque 11A",
        art: "decanato",
        walkable: [
            { x: 90, y: 255 },
            { x: 870, y: 255 },
            { x: 870, y: 495 },
            { x: 90, y: 495 },
        ],
        spawn: { x: 420, y: 390 },
        elements: [
            {
                id: "decanato-ventanilla",
                kind: "mesa",
                rect: { x: 330, y: 270, width: 300, height: 70 },
            },
            {
                id: "decanato-espera",
                kind: "silla",
                rect: { x: 400, y: 350, width: 90, height: 60 },
                surface: {
                    polygon: [
                        { x: 412, y: 372 },
                        { x: 478, y: 372 },
                        { x: 478, y: 400 },
                        { x: 412, y: 400 },
                    ],
                    height: 16,
                    accessEdges: [],
                    fallsAtEdge: true,
                },
            },
            {
                id: "decanato-plazoleta",
                kind: "puerta",
                label: "Volver a la plazoleta",
                rect: { x: 150, y: 380, width: 150, height: 60 },
                shape: "arch",
                to: "plazoleta",
            },
            {
                id: "decanato-parqueo",
                kind: "puerta",
                label: "Salida · Parqueo FIEC",
                rect: { x: 660, y: 380, width: 150, height: 60 },
                shape: "arch",
                to: "parqueo",
            },
        ],
    },
    /* Donde termina la facultad: parqueo y vía interna. Tres salidas. */
    parqueo: {
        id: "parqueo",
        name: "Parqueo FIEC",
        art: "parqueo",
        walkable: [
            { x: 150, y: 250 },
            { x: 810, y: 250 },
            { x: 915, y: 505 },
            { x: 45, y: 505 },
        ],
        spawn: { x: 480, y: 390 },
        elements: [
            {
                /* Cruzar la calle es cruzar esta zona: por eso está arriba, del lado
                   contrario a la facultad, y no pegada a la puerta del 11A. */
                id: "parqueo-parque",
                kind: "puerta",
                label: "Cruzar al Parque ¡Ajá!",
                rect: { x: 610, y: 275, width: 150, height: 60 },
                shape: "rect",
                to: "parque",
            },
            {
                id: "parqueo-fimcp",
                kind: "puerta",
                label: "FIMCP",
                rect: { x: 200, y: 275, width: 150, height: 60 },
                shape: "arch",
                to: "fimcp",
            },
            {
                /* A las bandas, por lo mismo que en la plazoleta: en el centro tapaban la línea
                   del spawn a las dos puertas de arriba, y con mobiliario sólido eso las dejaba
                   muertas. Acá el escenario dibuja el suyo en x 900. */
                id: "parqueo-arbol-1",
                kind: "arbol",
                rect: { x: 70, y: 300, width: 70, height: 80 },
            },
            {
                id: "parqueo-arbol-2",
                kind: "arbol",
                rect: { x: 860, y: 300, width: 70, height: 80 },
            },
            {
                id: "parqueo-decanato",
                kind: "puerta",
                label: "Entrar al 11A",
                rect: { x: 150, y: 380, width: 150, height: 60 },
                shape: "arch",
                to: "decanato",
            },
            {
                id: "parqueo-paradero",
                kind: "puerta",
                label: "Avenida · Paradero",
                rect: { x: 660, y: 380, width: 150, height: 60 },
                shape: "rect",
                to: "paradero",
            },
        ],
    },
    /*
     * El Parque ¡Ajá!: área verde con experimentos de física al aire libre.
     *
     * El experimento del centro es lo que abre el minijuego. Está en medio a
     * propósito — es lo que se viene a hacer aquí — y las bancas quedan a los lados.
     */
    parque: {
        id: "parque",
        name: "Parque ¡Ajá!",
        art: "parque",
        walkable: [
            { x: 150, y: 250 },
            { x: 810, y: 250 },
            { x: 915, y: 505 },
            { x: 45, y: 505 },
        ],
        spawn: { x: 430, y: 400 },
        elements: [
            {
                id: "parque-arbol-1",
                kind: "arbol",
                rect: { x: 200, y: 275, width: 70, height: 80 },
            },
            {
                id: "parque-arbol-2",
                kind: "arbol",
                rect: { x: 620, y: 275, width: 70, height: 80 },
            },
            {
                id: "parque-experimento",
                kind: "juego",
                gameId: "aja",
                rect: { x: 380, y: 275, width: 130, height: 90 },
            },
            {
                id: "parque-stem",
                kind: "puerta",
                label: "Edificio STEM",
                rect: { x: 660, y: 380, width: 150, height: 60 },
                shape: "arch",
                to: "stem",
            },
            {
                // Abajo y no arriba porque el piso es un trapecio que se estrecha al fondo: a la
                // altura de los árboles, un rect de 150 se sale por el este.
                id: "parque-coliseo",
                kind: "puerta",
                label: "Coliseo Nuevo",
                rect: { x: 720, y: 440, width: 150, height: 60 },
                shape: "arch",
                to: "coliseo",
            },
            {
                id: "parque-banca",
                kind: "silla",
                rect: { x: 160, y: 385, width: 90, height: 60 },
                surface: {
                    polygon: [
                        { x: 172, y: 407 },
                        { x: 238, y: 407 },
                        { x: 238, y: 435 },
                        { x: 172, y: 435 },
                    ],
                    height: 16,
                    accessEdges: [],
                    fallsAtEdge: true,
                },
            },
            {
                id: "parque-parqueo",
                kind: "puerta",
                label: "Volver al parqueo",
                rect: { x: 405, y: 410, width: 150, height: 60 },
                shape: "rect",
                to: "parqueo",
            },
        ],
    },
    /* Investigación y posgrados: el edificio grande y moderno junto al parque. */
    stem: {
        id: "stem",
        name: "Edificio STEM",
        art: "stem",
        walkable: [
            { x: 90, y: 255 },
            { x: 870, y: 255 },
            { x: 870, y: 495 },
            { x: 90, y: 495 },
        ],
        spawn: { x: 420, y: 400 },
        elements: [
            {
                id: "stem-mesa-1",
                kind: "mesa",
                rect: { x: 380, y: 280, width: 160, height: 70 },
            },
            {
                id: "stem-mesa-2",
                kind: "mesa",
                rect: { x: 600, y: 280, width: 160, height: 70 },
            },
            {
                id: "stem-parque",
                kind: "puerta",
                label: "Salir al parque",
                rect: { x: 150, y: 380, width: 150, height: 60 },
                shape: "arch",
                to: "parque",
            },
            {
                /* El STEM era un callejón sin salida: una sola puerta y de vuelta por donde
                   viniste. Los contenedores le dan un para qué, y de paso el patio de clubes
                   cuelga de un sitio que ya se visita. */
                id: "stem-contenedores",
                kind: "puerta",
                label: "Patio de contenedores",
                rect: { x: 660, y: 380, width: 150, height: 60 },
                shape: "arch",
                to: "contenedores",
            },
            {
                /* Arrimada a la mesa y FUERA del pasillo.
                   Estaba en medio del camino entre las dos puertas —y encima montada sobre la de
                   los contenedores— así que ir de un lado al otro del STEM era ir contra una
                   silla: el deslizamiento la esquivaba y volvía a chocar con el umbral. */
                id: "stem-silla",
                kind: "silla",
                rect: { x: 790, y: 290, width: 70, height: 67 },
                surface: {
                    polygon: [
                        { x: 802, y: 312 },
                        { x: 848, y: 312 },
                        { x: 848, y: 340 },
                        { x: 802, y: 340 },
                    ],
                    height: 16,
                    accessEdges: [],
                    fallsAtEdge: true,
                },
            },
        ],
    },
    /*
     * El paradero. La puerta a UBEP es la del bus: se sube uno y aparece allá.
     *
     * Es una puerta normal y no un transporte con espera — el viaje se cuenta con el
     * dibujo del bus y con la etiqueta, no con una mecánica nueva que habría que
     * defender en el servidor.
     */
    paradero: {
        id: "paradero",
        name: "Paradero de buses",
        art: "paradero",
        walkable: [
            { x: 150, y: 250 },
            { x: 810, y: 250 },
            { x: 915, y: 505 },
            { x: 45, y: 505 },
        ],
        spawn: { x: 400, y: 420 },
        elements: [
            {
                id: "paradero-banca-1",
                kind: "silla",
                rect: { x: 330, y: 300, width: 90, height: 60 },
                surface: {
                    polygon: [
                        { x: 342, y: 322 },
                        { x: 408, y: 322 },
                        { x: 408, y: 350 },
                        { x: 342, y: 350 },
                    ],
                    height: 16,
                    accessEdges: [],
                    fallsAtEdge: true,
                },
            },
            {
                id: "paradero-banca-2",
                kind: "silla",
                rect: { x: 450, y: 300, width: 90, height: 60 },
                surface: {
                    polygon: [
                        { x: 462, y: 322 },
                        { x: 528, y: 322 },
                        { x: 528, y: 350 },
                        { x: 462, y: 350 },
                    ],
                    height: 16,
                    accessEdges: [],
                    fallsAtEdge: true,
                },
            },
            {
                id: "paradero-parqueo",
                kind: "puerta",
                label: "Volver al parqueo",
                rect: { x: 150, y: 380, width: 150, height: 60 },
                shape: "rect",
                to: "parqueo",
            },
            {
                /* La zona cae justo sobre la puerta del bus que dibuja el escenario: subirse
                   tiene que ser pisar el estribo que se ve, no tocar aire al lado. */
                id: "paradero-ubep",
                kind: "puerta",
                label: "Tomar el bus · UBEP",
                rect: { x: 660, y: 380, width: 150, height: 60 },
                shape: "rect",
                to: "ubep",
            },
        ],
    },
    /* Bienestar Estudiantil. Del mismo complejo cuelga el coliseo, a pocos metros. */
    ubep: {
        id: "ubep",
        name: "UBEP",
        art: "ubep",
        walkable: [
            { x: 90, y: 255 },
            { x: 870, y: 255 },
            { x: 870, y: 495 },
            { x: 90, y: 495 },
        ],
        /*
         * Delante de la silla de espera, no ENCIMA.
         *
         * Estaba en (430,400), que es el borde mismo de `ubep-silla`: se aterrizaba pegado a
         * un mueble sólido, y el primer paso hacia el coliseo chocaba antes de empezar. La
         * puerta no llegaba a dispararse nunca.
         */
        spawn: { x: 430, y: 460 },
        elements: [
            {
                id: "ubep-recepcion",
                kind: "mesa",
                rect: { x: 350, y: 275, width: 260, height: 70 },
            },
            {
                id: "ubep-silla",
                kind: "silla",
                rect: { x: 430, y: 355, width: 90, height: 60 },
                surface: {
                    polygon: [
                        { x: 442, y: 377 },
                        { x: 508, y: 377 },
                        { x: 508, y: 405 },
                        { x: 442, y: 405 },
                    ],
                    height: 16,
                    accessEdges: [],
                    fallsAtEdge: true,
                },
            },
            {
                id: "ubep-paradero",
                kind: "puerta",
                label: "Tomar el bus · Paradero",
                rect: { x: 150, y: 380, width: 150, height: 60 },
                shape: "rect",
                to: "paradero",
            },
            {
                id: "ubep-coliseo",
                kind: "puerta",
                label: "Coliseo Nuevo",
                rect: { x: 660, y: 380, width: 150, height: 60 },
                shape: "arch",
                to: "coliseo",
            },
        ],
    },
    /*
     * El Coliseo Nuevo: la cancha, sobre el fondo ilustrado (`art/rooms/coliseo.jpg`).
     *
     * Todo lo de esta sala está medido sobre esa imagen, así que si se cambia el fondo
     * hay que volver a pasar por F2. El piso sigue la base de las gradas del fondo —una
     * línea en perspectiva que baja hacia la derecha— y por abajo a la izquierda se
     * detiene antes de las gradas del primer plano.
     *
     * Las gradas y la mesa ya vienen dibujadas en la imagen, así que sus objetos llevan
     * `backdrop: true`: aportan el clic, la colisión y el asiento, pero no pintan nada.
     */
    coliseo: {
        id: "coliseo",
        name: "Coliseo Nuevo",
        art: "coliseo",
        walkable: [
            { x: 0, y: 242 },
            { x: 286, y: 225 },
            { x: 960, y: 315 },
            { x: 955, y: 532 },
            { x: 243, y: 532 },
            { x: 0, y: 348 },
        ],
        spawn: { x: 480, y: 390 },
        elements: [
            {
                /* Una grada es una silla larga: misma clase, otro rect. Es literalmente la
                   regla del catálogo — lo que se hereda es el tipo, nunca la instancia.
        
                   No estorba (`solid: false`), que es el resquicio que el tipo silla deja
                   para un banco corrido: el asiento es la franja de piso al pie de la grada, y
                   la altura sube el sprite hasta la primera fila. El contorno cubre las filas
                   de abajo, que es donde uno hace clic para sentarse. */
                id: "coliseo-grada-1",
                kind: "silla",
                rect: { x: 290, y: 190, width: 255, height: 80 },
                shape: "polygon",
                points: [
                    { x: 290, y: 190 },
                    { x: 545, y: 224 },
                    { x: 545, y: 270 },
                    { x: 290, y: 237 },
                ],
                solid: false,
                backdrop: true,
                surface: {
                    polygon: [
                        { x: 295, y: 228 },
                        { x: 540, y: 261 },
                        { x: 540, y: 269 },
                        { x: 295, y: 236 },
                    ],
                    height: 24,
                    accessEdges: [],
                    fallsAtEdge: true,
                },
            },
            {
                /* Más alta que la primera porque está más cerca: en la imagen, el frente de la
                   grada crece de 16 a 22 px hacia la derecha. */
                id: "coliseo-grada-2",
                kind: "silla",
                rect: { x: 625, y: 228, width: 335, height: 97 },
                shape: "polygon",
                points: [
                    { x: 625, y: 228 },
                    { x: 960, y: 272 },
                    { x: 960, y: 325 },
                    { x: 625, y: 281 },
                ],
                solid: false,
                backdrop: true,
                surface: {
                    polygon: [
                        { x: 630, y: 273 },
                        { x: 955, y: 316 },
                        { x: 955, y: 325 },
                        { x: 630, y: 282 },
                    ],
                    height: 30,
                    accessEdges: [],
                    fallsAtEdge: true,
                },
            },
            {
                /* La mesa de control, entre las dos gradas. Viene en el fondo y sólo estorba. */
                id: "coliseo-mesa-control",
                kind: "mesa",
                rect: { x: 547, y: 249, width: 48, height: 17 },
                backdrop: true,
            },
            {
                /* El balón en el círculo central: tocarlo abre el partido y deja al jugador
                   esperando rival hasta que otro toque el mismo balón. Es un objeto y no una zona
                   del piso para que cruzar la cancha caminando no abra el volley sin querer. */
                id: "coliseo-cancha",
                kind: "cancha",
                rect: { x: 355, y: 292, width: 30, height: 32 },
                art: "balon-volley",
            },
            {
                /* A un costado, sobre la franja roja que sale de cuadro: se sale por donde la
                   cancha sigue, y la puerta no tapa nada de la zona de juego. */
                id: "coliseo-ubep",
                kind: "puerta",
                label: "Salir a UBEP",
                rect: { x: 8, y: 268, width: 60, height: 62 },
                shape: "arch",
                to: "ubep",
            },
            {
                // Cierra el anillo largo. El coliseo estaba a seis puertas del inicio: nadie llegaba
                // por casualidad, y a un sitio de encuentro eso lo mata.
                id: "coliseo-parque",
                kind: "puerta",
                label: "Parque ¡Ajá!",
                rect: { x: 190, y: 409, width: 150, height: 60 },
                shape: "arch",
                to: "parque",
            },
            {
                id: "coliseo-taws",
                kind: "puerta",
                label: "11A ",
                rect: { x: 736, y: 358, width: 223, height: 172 },
                shape: "polygon",
                points: [
                    { x: 959, y: 358 },
                    { x: 951, y: 530 },
                    { x: 736, y: 529 },
                ],
                entry: { x: 833, y: 441 },
                to: "salida11a",
            },
        ],
    },
    /*
     * El taller: la sala desde la que se edita el mapa, y que no es parte del juego.
     *
     * Su escenario es una rejilla neutra a propósito. Acá no se viene a estar: se viene a
     * mirar el escenario de OTRA sala con su polígono encima, y un fondo con carácter
     * propio pelearía con lo que se está tratando de ver. La rejilla además es una regla:
     * se puede contar cuántos píxeles mide lo que estás arrastrando.
     *
     * Tiene piso y puerta como cualquier otra porque `CampusRoom` no admite menos —una
     * sala sin sitio donde pararse es una sala donde el join falla— y porque salir tiene
     * que ser posible sin recargar.
     */
    taller: {
        id: "taller",
        name: "Taller de mapas",
        art: "taller",
        walkable: [
            { x: 60, y: 200 },
            { x: 900, y: 200 },
            { x: 900, y: 520 },
            { x: 60, y: 520 },
        ],
        spawn: { x: 480, y: 460 },
        elements: [
            {
                /* Abajo a la izquierda, lejos del centro: el centro es donde se previsualiza
                   la sala que se está editando, y una puerta ahí taparía justo lo que se mira. */
                id: "taller-salida",
                kind: "puerta",
                label: "Volver al campus",
                rect: { x: 90, y: 440, width: 150, height: 60 },
                shape: "arch",
                to: "taws",
            },
        ],
    },
    contenedores: {
        id: "contenedores",
        name: "Patio de contenedores",
        art: "contenedores",
        walkable: [
            { x: 90, y: 255 },
            { x: 870, y: 255 },
            { x: 870, y: 495 },
            { x: 90, y: 495 },
        ],
        /* Abajo y al centro, en línea recta con el directorio: llegar hasta él es lo único
           que hay que hacer acá, y el movimiento sólo sabe ir derecho. */
        spawn: { x: 480, y: 440 },
        elements: [
            {
                /*
                 * El directorio: la garita del patio, y la única forma de entrar a una sede.
                 *
                 * Sin `facultyId` porque éste los enseña TODOS — es el directorio general del
                 * campus. Los de cada facultad viven en su edificio y filtran por ella, que es
                 * lo que hace que un club tenga sede y no sólo una sala.
                 */
                id: "contenedores-directorio",
                kind: "directorio",
                rect: { x: 400, y: 285, width: 160, height: 96 },
            },
            {
                /* A la izquierda y abajo, lejos de la fila: la salida no compite con los
                   contenedores por el mismo clic. */
                id: "contenedores-stem",
                kind: "puerta",
                label: "Volver al STEM",
                rect: { x: 110, y: 410, width: 150, height: 60 },
                shape: "arch",
                to: "stem",
            },
        ],
    },
};
export const ROOM_LIST = ROOM_IDS.map((id) => ROOMS[id]);
/*
 * El patio de contenedores.
 *
 * En ESPOL los contenedores son eso: cajones metálicos reciclados como aulas y oficinas.
 * Acá son la sede de cada club, y este patio es lo que las hace un SITIO al que se va en
 * vez de un botón en un panel.
 *
 * Los cinco elementos `club` no son puertas, y no podían serlo: una puerta lleva a un
 * `RoomId` del catálogo, y la sala de un club se arma con lo que el club tenga puesto —
 * hay una por club y ninguna está en `ROOMS`. Y hay algo que ninguna puerta del campus
 * hace: **éstos miran quién eres**. Se ven todos, se tocan todos, y sólo se entra al de
 * un club del que eres miembro. Ver `mayUse` en `CampusRoom`.
 */
export function isRoomId(value) {
    return typeof value === "string" && ROOM_IDS.includes(value);
}
export function getRoom(id) {
    return isRoomId(id) ? ROOMS[id] : undefined;
}
/** ¿Este elemento es una puerta? Estrecha el tipo, así que `to` queda accesible. */
export function isDoor(element) {
    return element.kind === "puerta";
}
/** Las puertas de una sala, en orden de catálogo. */
export function doorsOf(room) {
    return room.elements.filter(isDoor);
}
/**
 * Un elemento de cualquier parte del catálogo, con la sala que lo contiene.
 *
 * La búsqueda es global porque los ids lo son, y `validateCatalog` lo impone: sin esa
 * garantía esto devolvería el primero que coincidiera y el resto quedaría en sombra.
 */
export function findElement(elementId) {
    if (typeof elementId !== "string")
        return undefined;
    for (const room of ROOM_LIST) {
        const element = room.elements.find((candidate) => candidate.id === elementId);
        if (element)
            return { element, from: room };
    }
    return undefined;
}
/**
 * El elemento sobre el que está parado un punto, si hay alguno.
 *
 * Recorre en orden inverso para que el último dibujado gane, que es el que se ve
 * encima: si dos zonas se solapan, elegir la de abajo devolvería algo que el jugador
 * no puede ni ver.
 */
export function elementAt(room, point) {
    for (let i = room.elements.length - 1; i >= 0; i--) {
        const element = room.elements[i];
        if (element && elementContains(element, point))
            return element;
    }
    return undefined;
}
/** La puerta que se cruza al caminar dentro, sin hacer clic: las invisibles. */
export function silentDoorAt(room, point) {
    return doorsOf(room).find((door) => door.hidden && elementContains(door, point));
}
/**
 * La puerta de `room` que lleva de vuelta a `origin`, si la hay.
 *
 * Es lo que convierte "vengo de FEPOL" en "aparezco en el umbral que devuelve a
 * FEPOL". Un pasaje de un solo sentido no tiene vuelta y devuelve undefined, que es
 * lo que hace caer al spawn.
 */
export function doorBackTo(room, origin) {
    return doorsOf(room).find((door) => door.to === origin);
}
/*
 * Reexportados desde `elements.js` a propósito.
 *
 * Medio proyecto los llamaba como `doorOutline`, `doorContains` y `doorCentre`, y
 * ahora la geometría es la misma para cualquier objeto. Reexportar deja un solo
 * nombre por concepto sin obligar a cada consumidor a saber en qué módulo cayó.
 */
export { elementCenter, elementContains, elementOutline };
//# sourceMappingURL=rooms.js.map